const { newGuid } = require('../../utils');
var DomainException = require('../DomainException');
const AggregateRoot = require('../ddd/AggregateRoot');
const _ = require('../../lodash');

const getConstants = require('../../utils/getConstants');
const constants = getConstants();
const { formItemTypes, bindingTypes, answerStates, repeaterFormItemTypes, writeScopes } = constants;
const moment = require('moment-timezone');

const entryTypeLookup = require('./entryTypeLookup');
const RuleEvaluator = require('../forms/utilities/RuleEvaluator');
const isInactiveBySkippedRepeaterAncestor = require('../forms/utilities/isInactiveBySkippedRepeaterAncestor');
const classifyRepeaterEntryMode = require('../forms/utilities/classifyRepeaterEntryMode');
const { createResolver } = require('../tasks/utilities/ValueResolver');

class Submission extends AggregateRoot {
   constructor({ viewPortComponentBuilder, questionBuilder, user, logger }) {
      super({ user, logger });
      this.viewPortComponentBuilder = viewPortComponentBuilder;
      this.questionBuilder = questionBuilder;
      this._entries = [];
      this._children = [];
      this.ruleEvaluator = new RuleEvaluator();
      this._resolver = null;
   }

   get agt() {
      return 'Submission';
   }

   getResolver() {
      return this._resolver;
   }

   _rebuildResolver() {
      this._resolver = createResolver(this._properties.items || [], { debug: false });
   }

   async createNew({ idSubmission, noSubmission, noForm, noLocation = null }) {
      const tid = this.domainUser.tid;
      const idChannel = newGuid();

      let p = {
         aid: idSubmission,
         noSubmission,
         noForm,
         noLocation,
         idChannel,
         score: null,
         maxScore: null,
         tid,
         items: []
      };

      this.apply('SUBMISSION_CREATED', p);

      var o = _.omit(p, ['aid', 'tid', 'noForm', 'idChannel']);

      this.setData(
         {
            properties: _.merge(
               {
                  id: p.aid
               },
               o,
               {
                  form: {
                     noForm
                  },
                  channel: {
                     id: idChannel,
                     count: 0
                  }
               }
            )
         },
         true
      );
   }

   isInactiveBySkippedRepeaterAncestor({ idItem }) {
      return isInactiveBySkippedRepeaterAncestor(this._properties.items, idItem);
   }

   guardInactiveBySkippedRepeaterAncestor({ idItem }) {
      if (this.isInactiveBySkippedRepeaterAncestor({ idItem })) {
         throw new DomainException('Cannot modify an item beneath a skipped Repeater.');
      }
   }

   guardCanonicalFullListMembershipMutation({ repeater }) {
      const options = repeater?.options || {};
      const classification = classifyRepeaterEntryMode({
         idRepeaterType: options.idRepeaterType,
         entryMode: options.entryMode,
         idRepeaterCadence: options.idRepeaterCadence,
         timesToRepeat: options.timesToRepeat
      });

      if (classification.kind === classifyRepeaterEntryMode.kinds.CANONICAL_FULL_LIST) {
         throw new DomainException('Cannot modify canonical Full list membership after materialisation.');
      }
   }

   guardSkippedRepeaterTarget({ idItem }) {
      const entry = this.guardMissingEntry(idItem);
      const isRepeater = entry.item.idItemType === formItemTypes.REPEATER.id;
      const isSkipped = (entry.item.state & answerStates.SKIPPED) === answerStates.SKIPPED;

      if (isRepeater && isSkipped) {
         throw new DomainException('Cannot modify a skipped Repeater.');
      }
   }

   addItemComment({ idItem, idFrame, comment, actionedOn }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.addComment({ idFrame, comment, actionedOn });

      entry.validate();

      this.refreshRepeaterChain({ idItem });

      this._properties.channel.count += 1;
   }

   deleteItemComment({ idItem, idFrame, actionedOn }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.deleteComment({ idFrame, actionedOn });

      entry.validate();

      this.refreshRepeaterChain({ idItem });

      this._properties.channel.count -= 1;
      if (this._properties.channel.count < 0) {
         this._properties.channel.count = 0;
      }
   }

   skipItem({ idItem, actionedOn }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });
      entry.skip({ actionedOn });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
      this.updateStats();
   }

   unskipItem({ idItem, actionedOn }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });
      entry.unskip({ actionedOn });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
      this.updateStats();
   }

   updateDateTimeField({ idItem, actionedOn, datetime, time, geoLat, geoLong, geoTimeStamp }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.DATETIME);

      entry.update({ actionedOn, datetime, time, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   updateNumericField({ idItem, actionedOn, number, geoLat, geoLong, geoTimeStamp }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.NUMBER);

      entry.update({ actionedOn, number, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   updatePhotoFieldAssetUris({ idItem, idAsset, localUri, remoteUri }) {
      let entry = this.guardMissingEntry(idItem);

      entry.ensureIsItemType(formItemTypes.PHOTO);

      entry.updateAssetUris({ idAsset, localUri, remoteUri });
   }

   updatePhotoField({ idItem, actionedOn, images, geoLat, geoLong, geoTimeStamp }) {
      let entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.PHOTO);

      entry.update({ actionedOn, images, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   updateOptionsField({ idItem, actionedOn, selectedOptions, geoLat, geoLong, geoTimeStamp }) {
      const entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.OPTIONS);

      entry.update({ actionedOn, selectedOptions, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   updateTextField({ idItem, actionedOn, text, geoLat, geoLong, geoTimeStamp }) {
      const entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.TEXT);

      entry.update({ actionedOn, text, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   changeRepeaterGroupListItem({ idItem, idListItem, actionedOn, geoLat, geoLong, geoTimeStamp }) {
      const entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.REPEATER_GROUP);

      const repeater = this.guardMissingItem(entry.item.idParent);
      this.guardCanonicalFullListMembershipMutation({ repeater });

      entry.changeRepeaterGroupListItem({ actionedOn, idListItem, geoLat, geoLong, geoTimeStamp });

      this.refreshRepeaterChain({ idItem });
   }

   getRepeatinGroupEntryIfAny({ idItem }) {
      if (!idItem) {
         return null;
      }
      const entry = this.guardMissingEntry(idItem);
      return entry.isOfItemType(formItemTypes.REPEATER_GROUP) ? entry : null;
   }

   refreshRepeaterChain({ idItem }) {
      let currentEntry = null;
      let repeaterEntry = null;
      let idItemCurrent = idItem;

      do {
         currentEntry = this.guardMissingEntry(idItemCurrent);
         repeaterEntry = this.getRepeatinGroupEntryIfAny({ idItem: idItemCurrent });

         if (repeaterEntry) {
            repeaterEntry.refresh();
         }
         idItemCurrent = currentEntry.item.idParent;
      } while (idItemCurrent != null);
   }

   refreshValidityChain({ idItem }) {
      let currentEntry = this.guardMissingEntry(idItem);
      let idParent = currentEntry.item.idParent;

      while (idParent != null) {
         currentEntry = this.guardMissingEntry(idParent);

         if (typeof currentEntry.refreshValidityFromChildren === 'function') {
            currentEntry.refreshValidityFromChildren();
         }

         idParent = currentEntry.item.idParent;
      }
   }

   markToDoAsComplete({ idItem, actionedOn, geoLat, geoLong, geoTimeStamp }) {
      const entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.TODO);

      entry.markComplete({ actionedOn, geoLat, geoLong, geoTimeStamp });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   markToDoAsIncomplete({ idItem, actionedOn }) {
      const entry = this.guardMissingEntry(idItem);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem });

      entry.ensureIsItemType(formItemTypes.TODO);

      entry.markIncomplete({ actionedOn });

      entry.validate();

      this.refreshRepeaterChain({ idItem });
      this.refreshValidityChain({ idItem });
   }

   refreshAll() {
      this.refresh(this._children);
   }

   refresh(children) {
      for (var i = 0; i < children.length; i++) {
         const e = children[i];
         if (e.refresh) {
            e.refresh();
         }
         if (e.children) {
            this.refresh(e.children);
         }
      }
   }

   setupAsBlank({ items }) {
      let givenItems = items || [];

      var uniqueItems = _.uniqBy(givenItems, (x) => {
         return x.id;
      });

      if (uniqueItems.length != givenItems.length) {
         throw new DomainException('All given item ids must be unique.');
      }

      const now = moment.utc().toDate();

      for (let j = 0; j < items.length; j++) {
         let i = items[j];
         i.no = j + 1;
         i.upd = i.upd || now;
         i.geoLat = i.geoLat || null;
         i.geoLong = i.geoLong || null;
         i.geoTimeStamp = i.geoTimeStamp || null;
         i.noOfComments = i.noOfComments || 0;
      }

      var p = { items, noSubmission: this._properties.noSubmission };

      this.apply('SUBMISSION_ITEMS_SETUP', p);

      this._properties.items = p.items;

      this._rebuildResolver();
   }

   addRepeaterGroup({ idRepeaterGroup, idRepeaterItem, items, actionedOn }) {
      const repeaterEntry = this.guardMissingEntry(idRepeaterItem);
      repeaterEntry.ensureIsItemType(formItemTypes.REPEATER);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem: idRepeaterItem });
      this.guardSkippedRepeaterTarget({ idItem: idRepeaterItem });

      let givenItems = items || [];

      var uniqueItems = _.uniqBy(givenItems, (x) => {
         return x.id;
      });

      if (uniqueItems.length != givenItems.length) {
         throw new DomainException('All given item ids must be unique.');
      }

      const maxNo = this.getNextNo();

      for (let j = 0; j < items.length; j++) {
         let i = items[j];
         i.no = maxNo + j + 1;
         i.upd = i.upd || actionedOn;
         i.geoLat = i.geoLat || null;
         i.geoLong = i.geoLong || null;
         i.geoTimeStamp = i.geoTimeStamp || null;
         i.noOfComments = i.noOfComments || 0;
      }

      var p = { idRepeaterGroup, idRepeaterItem, items, noSubmission: this._properties.noSubmission };

      this.apply('REPEATER_GROUP_ADDED', p);

      this._properties.items = this._properties.items.concat(p.items);

      this._rebuildResolver();
   }

   removeRepeaterGroup({ idRepeaterItem, idRepeaterGroup, actionedOn: _actionedOn }) {
      const entry = this.guardMissingEntry(idRepeaterItem);

      entry.ensureIsItemType(formItemTypes.REPEATER);
      this.guardCanonicalFullListMembershipMutation({ repeater: entry.item });
      this.guardMissingEntry(idRepeaterGroup);
      this.guardInactiveBySkippedRepeaterAncestor({ idItem: idRepeaterGroup });

      var childItems = this.getChildItems(this._properties.items, idRepeaterGroup);
      var childIdsRemoved = _.map(childItems, 'id');

      var p = { idRepeaterItem, idRepeaterGroup, childIdsRemoved, noSubmission: this._properties.noSubmission };

      this.apply('REPEATER_GROUP_REMOVED', p);

      let idsToRemove = childIdsRemoved.concat(idRepeaterGroup);
      this._properties.items = this._properties.items.filter((item) => !idsToRemove.includes(item.id));

      this._rebuildResolver();
   }

   getNextNo() {
      let noMax = 1;
      if (this._properties.items.length > 0) {
         var sorted = _.sortBy(this._properties.items, (ag) => {
            return ag.no;
         });

         noMax = sorted.reverse()[0].no;
      }
      return noMax;
   }

   getFormNo() {
      return this._properties.form.noForm;
   }

   getChannelId() {
      return this._properties.channel.id;
   }

   getLocationNo() {
      return this._properties.noLocation;
   }

   getSubmissionNo() {
      return this._properties.noSubmission;
   }

   getSubmissionItem({ idItem }) {
      return this.guardMissingItem(idItem);
   }

   hydrate() {
      // Sets up entries from existing items
      this.setup({});
   }

   getNextRepeaterGroupMeta({ idItem }) {
      const entry = this.guardMissingEntry(idItem);
      return entry.getNextRepeaterGroupMeta();
   }

   setup({ formSnapshot = null, noLocation = -1, resources = [], locations = [], datasources = [] }) {
      if (formSnapshot != null) {
         var res = this.questionBuilder.build({
            noLocation,
            formSnapshot,
            submissionSnapshot: this.getSnapshot(),
            resources,
            locations,
            datasources
         });

         const items = res.questions;
         this._properties.items = items;
      }

      this._rebuildResolver();

      // remove existing entries
      _.each(this._entries, (e) => {
         e.unwire();
      });
      this._entries.length = 0;
      this._children.length = 0;

      // Builds the view port components to display in a tree structure
      // which is how they will be presented and how the entries relate to each other
      this._properties.items = this.viewPortComponentBuilder.getNodesFromARoot(this._properties.items, null);
      const result = this.viewPortComponentBuilder.build({ items: this._properties.items });

      _.each(result.viewport, (o) => {
         const entry = new entryTypeLookup[o.componentType](this, o, this._properties.items, this._entries, this.ruleEvaluator);
         this._entries.push(entry);
         this._children.push(entry);
         entry.createChildEntries(entryTypeLookup);
      });
   }

   lock() {
      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];
         e.lock();
      }
   }

   unlock() {
      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];
         e.unlock();
      }
   }

   hasFailures() {
      let isFailed = false;

      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];
         if (e.hasFailures()) {
            isFailed = true;
         }
      }
      return isFailed;
   }

   countFlags() {
      let flagged = false;
      let noOfFlags = 0;

      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];

         const flagResult = e.countFlags();

         if (flagResult.flagged) {
            flagged = true;
            noOfFlags = noOfFlags + (flagResult.noOfFlags || 1);
         }
      }

      return {
         flagged,
         noOfFlags
      };
   }

   validate() {
      let isValid = true;

      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];
         if (!e.validate()) {
            isValid = false;
         }
      }
      return isValid;
   }

   score() {
      let overallScore = null;
      let overallMaxScore = null;

      for (var i = 0; i < this._children.length; i++) {
         const e = this._children[i];
         const scoreResult = e.score();

         overallScore =
            scoreResult.score == null ? overallScore : overallScore == null ? scoreResult.score : overallScore + scoreResult.score;

         overallMaxScore =
            scoreResult.maxScore == null
               ? overallMaxScore
               : overallMaxScore == null
               ? scoreResult.maxScore
               : overallMaxScore + scoreResult.maxScore;
      }

      this._properties.score = overallScore;
      this._properties.maxScore = overallMaxScore;

      return {
         score: overallScore,
         maxScore: overallMaxScore
      };
   }

   updateStats() {
      const scoreResult = this.score();
      const hasFailures = this.hasFailures();
      const flagResult = this.countFlags();

      const p = { noSubmission: this._properties.noSubmission, hasFailures, ...scoreResult, ...flagResult, items: this._properties.items };

      this.apply('SUBMISSION_STATS_UPDATED', p);

      return { hasFailures, ...scoreResult, ...flagResult };
   }

   materializeCalculatedFields({ actionedOn }) {
      const entries = this._entries || [];
      if (!entries.length) return;

      for (let i = 0; i < entries.length; i++) {
         const e = entries[i];
         if (!e || !e.item) continue;

         if (e.item.idItemType !== formItemTypes.CALCULATED.id) continue;
         if (this.isInactiveBySkippedRepeaterAncestor({ idItem: e.item.id })) continue;

         // Prefer: concrete entry implements materialize
         if (typeof e.materialize === 'function') {
            e.materialize({ actionedOn });
         }
      }
   }

   getDatasourceWriteBacks({ includeNulls = true } = {}) {
      const items = this._properties.items || [];
      if (items.length === 0) return [];

      // Helper: last-write-wins
      const dedup = {};
      const effectiveWriteScope = (writeScope) => {
         if (writeScope == null) return writeScopes.LOCATION;
         return writeScope;
      };
      const identityScope = (writeScope) => {
         if (writeScope === writeScopes.LOCATION || writeScope === writeScopes.GLOBAL) return writeScope;
         return `invalid:${String(writeScope)}`;
      };
      const keyOf = (noDataSource, rowKey, column, writeScope) => `${noDataSource}::${rowKey}::${column}::${identityScope(writeScope)}`;

      const isWriteBinding = (noBindingType) => noBindingType === bindingTypes.WRITE.id || noBindingType === bindingTypes.READWRITE.id;

      const getActionedOn = (it) => {
         const iso = (it.response && (it.response.upd || it.response.actionedOn)) || it.upd;
         return iso ? new Date(iso) : new Date(0);
      };

      // Traverse the entry tree to find repeaters
      const visitEntries = (entries, visitor) => {
         for (let i = 0; i < entries.length; i++) {
            const e = entries[i];
            visitor(e);
            if (e.children && e.children.length) visitEntries(e.children, visitor);
         }
      };

      visitEntries(this._children || [], (entry) => {
         if (!entry || !entry.item) return;
         if (entry.item.idItemType !== formItemTypes.REPEATER.id) return;

         const options = entry.item.options || {};
         const isListRepeater = options.idRepeaterType == repeaterFormItemTypes.LIST.id;
         if (!isListRepeater) return; // retained Free-flow bindings are dormant configuration only

         // RepeaterEntry should expose the active List binding; retain the raw fallback for legacy List entries only.
         const dsRef = typeof entry.getDataSourceRef === 'function' ? entry.getDataSourceRef() : null;
         const binding = dsRef || options.binding;
         const noDataSource = binding && binding.noDataSource ? binding.noDataSource : null;
         if (!noDataSource) return; // not datasource-bound

         // Repeater groups are direct children entries whose itemType is REPEATER_GROUP
         const groupEntries = (entry.children || []).filter((c) => c && c.item && c.item.idItemType === formItemTypes.REPEATER_GROUP.id);

         groupEntries.forEach((groupEntry) => {
            const rowKey =
               typeof groupEntry.getRowKey === 'function'
                  ? groupEntry.getRowKey()
                  : groupEntry.item.response
                  ? groupEntry.item.response.val
                  : null;

            if (rowKey == null || rowKey === '') return; // fail-soft: no row selected

            // Walk descendants under this group and collect write-bound mappings
            const walker =
               typeof groupEntry.walkDescendants === 'function'
                  ? groupEntry.walkDescendants.bind(groupEntry)
                  : (visitor) => {
                       // fallback DFS if BaseEntry wasn't updated yet
                       const stack = (groupEntry.children || []).slice().reverse();
                       while (stack.length) {
                          const n = stack.pop();
                          visitor(n);
                          if (n.children && n.children.length) {
                             for (let i = n.children.length - 1; i >= 0; i--) stack.push(n.children[i]);
                          }
                       }
                    };

            walker((childEntry) => {
               if (!childEntry || !childEntry.item) return;
               const it = childEntry.item;

               const mapping = it.options && it.options.mapping ? it.options.mapping : null;
               if (!mapping) return;
               if (mapping.type !== 'lookup') return;
               if (!isWriteBinding(mapping.noBindingType)) return;

               const column = mapping.valueColumn;
               if (!column) {
                  throw new DomainException(`WRITE binding missing valueColumn (idItem=${it.id}, idFormItem=${it.idFormItem})`);
               }

               const value = it.response ? it.response.val : undefined;
               if (typeof value === 'undefined') return;
               if ((value === null || value === '') && !includeNulls) return;

               const actionedOn = getActionedOn(it);
               const writeScope = effectiveWriteScope(mapping.writeScope);

               const k = keyOf(noDataSource, rowKey.toString(), column, writeScope);
               const candidate = {
                  noDataSource,
                  // rowKey === DataSourceRow.data.id (business key)
                  rowKey: rowKey.toString(),
                  column,
                  value,
                  writeScope,
                  actionedOn,
                  provenance: {
                     idItem: it.id,
                     noItem: it.no,
                     idRepeater: entry.item.id,
                     idRepeaterGroup: groupEntry.item.id
                  }
               };

               const existing = dedup[k];
               if (!existing) {
                  dedup[k] = candidate;
                  return;
               }

               const exT = existing.actionedOn ? existing.actionedOn.getTime() : 0;
               const caT = candidate.actionedOn ? candidate.actionedOn.getTime() : 0;

               if (caT > exT) {
                  dedup[k] = candidate;
               } else if (caT === exT) {
                  const exNo = existing.provenance ? existing.provenance.noItem : -1;
                  const caNo = candidate.provenance ? candidate.provenance.noItem : -1;
                  if (caNo > exNo) dedup[k] = candidate;
               }
            });
         });
      });

      const out = _.values(dedup);
      out.sort((a, b) => {
         if (a.noDataSource !== b.noDataSource) return a.noDataSource.toString().localeCompare(b.noDataSource.toString());
         if (a.rowKey !== b.rowKey) return a.rowKey.localeCompare(b.rowKey);
         return a.column.localeCompare(b.column);
      });

      return out;
   }

   /* =================== */

   guardMissingEntry(idItem, message) {
      message = message || 'No such submission item exists.';
      var i = _.find(this._entries, { idItem: idItem.toLowerCase() });
      if (!i) {
         throw new DomainException(message);
      }
      return i;
   }

   guardMissingItem(idItem, message) {
      message = message || 'No such submission item exists.';
      var i = _.find(this._properties.items, { id: idItem.toLowerCase() });
      if (!i) {
         throw new DomainException(message);
      }
      return i;
   }

   getChildItems(items, idItem) {
      if (!idItem) {
         throw new Error('idItem is null');
      }
      let foundItems = [];
      var children = _.filter(items, (l) => {
         return l.idParent == idItem;
      });

      _.each(children, (n) => {
         foundItems.push(n);
         foundItems = foundItems.concat(this.getChildItems(items, n.no));
      });
      return foundItems;
   }

   setData(data, isnew) {
      super.setData(data, isnew);
   }
}

module.exports = Submission;
